import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { type Dayjs } from "dayjs";
import { useNotify, usePermissions, useRecordContext } from "react-admin";
import { AppDatePickerLocalization } from "@/components/AppDatePickerLocalization";
import { getApiUrl } from "@/constants";
import httpClient from "@/dataprovider/http-client";
import type { PermissionRecord } from "@/types/permissions";
import { formatDisplayDateTime, formatIsoDate, formatListDate } from "@/utils/date-format";
import { hasPermission } from "@/utils/permission-utils";
import {
  buildBumpConfirmCopy,
  buildCancelConfirmCopy,
  canScheduleAnotherStop,
  countOpenStops,
  deriveThaliDeliveryAlert,
  deliveryStopSourceLabel,
  deliveryStopStatusLabel,
  FMB_DELIVERY_STOP_MAX_OPEN,
  FMB_THALI_DELIVERY_CANCEL_PERMISSION,
  FMB_THALI_DELIVERY_EDIT_PERMISSION,
  groupStopsByThaliId,
  isOpenDeliveryStop,
  isRecord,
  parseCancelPreview,
  parseDeliveryStopPreview,
  parseStopsListResponse,
  shouldConfirmBump,
  type DeliveryStopCancelPreview,
  type DeliveryStopPreview,
  type DeliveryStopRow,
  type DeliveryStopStatus,
} from "./delivery-stop-form";

type ThaliRow = {
  id?: string;
  thaliNo?: string;
  isActive?: boolean;
  thaliType?: { name?: string };
};

type StopFormState = {
  mode: "create" | "edit";
  thali: ThaliRow;
  stop?: DeliveryStopRow;
  startDate: string;
  endDate: string;
  note: string;
  startLocked: boolean;
};

const STATUS_CHIP_COLOR: Record<DeliveryStopStatus, "info" | "warning" | "default"> = {
  SCHEDULED: "info",
  ACTIVE: "warning",
  COMPLETED: "default",
  CANCELLED: "default",
};

function requestErrorMessage(error: unknown, fallback: string): string {
  return isRecord(error) && typeof error.message === "string" ? error.message : fallback;
}

function YmdPicker({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (ymd: string) => void;
  disabled?: boolean;
}) {
  const parsed = value ? dayjs(value) : null;
  const handleChange = (next: Dayjs | null) => {
    if (next == null || !next.isValid()) {
      return;
    }
    onChange(formatIsoDate(next));
  };
  return (
    <AppDatePickerLocalization>
      <DatePicker
        label={label}
        value={parsed?.isValid() ? parsed : null}
        onChange={handleChange}
        disabled={disabled}
        slotProps={{ textField: { size: "small", fullWidth: true } }}
      />
    </AppDatePickerLocalization>
  );
}

export default function DeliveryTab() {
  const record = useRecordContext();
  const notify = useNotify();
  const { permissions } = usePermissions<PermissionRecord>();
  const thalis = Array.isArray(record?.thalis) ? (record!.thalis as ThaliRow[]) : [];
  const thaliIds = useMemo(
    () => thalis.map((thali) => thali.id).filter(Boolean) as string[],
    [thalis]
  );

  const canWrite = hasPermission(permissions, FMB_THALI_DELIVERY_EDIT_PERMISSION);
  const canCancel = hasPermission(permissions, FMB_THALI_DELIVERY_CANCEL_PERMISSION);

  const [stops, setStops] = useState<DeliveryStopRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<StopFormState | null>(null);
  const [bumpPreview, setBumpPreview] = useState<DeliveryStopPreview | null>(null);
  const [cancelPreview, setCancelPreview] = useState<{
    stop: DeliveryStopRow;
    preview: DeliveryStopCancelPreview;
  } | null>(null);

  const loadStops = useCallback(async () => {
    if (thaliIds.length === 0) {
      setStops([]);
      return;
    }
    setLoading(true);
    try {
      const res = await httpClient(
        `${getApiUrl()}/fmbThaliDelivery/stops?fmbThaliIds=${encodeURIComponent(thaliIds.join(","))}&limit=100&orderBy=createdAt&order=DESC`
      );
      setStops(parseStopsListResponse(res.json).rows);
    } catch (error: unknown) {
      notify(requestErrorMessage(error, "Failed to load delivery stops"), { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [notify, thaliIds]);

  useEffect(() => {
    void loadStops();
  }, [loadStops]);

  const stopsByThali = useMemo(() => groupStopsByThaliId(stops), [stops]);

  const openCreate = (thali: ThaliRow) => {
    setBumpPreview(null);
    setForm({
      mode: "create",
      thali,
      startDate: "",
      endDate: "",
      note: "",
      startLocked: false,
    });
  };

  const openEdit = (thali: ThaliRow, stop: DeliveryStopRow) => {
    setBumpPreview(null);
    setForm({
      mode: "edit",
      thali,
      stop,
      startDate: stop.startDate,
      endDate: stop.endDate,
      note: stop.note ?? "",
      startLocked: stop.status === "ACTIVE",
    });
  };

  const persistStop = async (preview: DeliveryStopPreview, confirmBump: boolean) => {
    if (!form) {
      return;
    }
    const payload = {
      startDate: preview.requestedStartDate,
      endDate: preview.requestedEndDate,
      note: form.note.trim() === "" ? null : form.note,
      confirmBump,
    };
    setBusy(true);
    try {
      if (form.mode === "create") {
        await httpClient(`${getApiUrl()}/fmbThaliDelivery/stops`, {
          method: "POST",
          body: JSON.stringify({ fmbThaliId: form.thali.id, ...payload }),
          headers: new Headers({ "Content-Type": "application/json" }),
        });
        notify("Delivery stop saved", { type: "success" });
      } else if (form.stop) {
        await httpClient(`${getApiUrl()}/fmbThaliDelivery/stops/${form.stop.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
          headers: new Headers({ "Content-Type": "application/json" }),
        });
        notify("Delivery stop updated", { type: "success" });
      }
      setForm(null);
      setBumpPreview(null);
      await loadStops();
    } catch (error: unknown) {
      notify(requestErrorMessage(error, "Failed to save delivery stop"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const previewAndSave = async () => {
    if (!form?.thali.id || !form.startDate || !form.endDate) {
      notify("Choose a from and to date", { type: "warning" });
      return;
    }
    setBusy(true);
    try {
      const path =
        form.mode === "create"
          ? `${getApiUrl()}/fmbThaliDelivery/stops/preview`
          : `${getApiUrl()}/fmbThaliDelivery/stops/${form.stop?.id}/preview`;
      const body =
        form.mode === "create"
          ? {
              fmbThaliId: form.thali.id,
              startDate: form.startDate,
              endDate: form.endDate,
              note: form.note,
            }
          : { startDate: form.startDate, endDate: form.endDate, note: form.note };
      const res = await httpClient(path, {
        method: "POST",
        body: JSON.stringify(body),
        headers: new Headers({ "Content-Type": "application/json" }),
      });
      const preview = parseDeliveryStopPreview(res.json);
      if (!preview) {
        notify("Could not preview the effective start date", { type: "error" });
        return;
      }
      setForm((current) =>
        current ? { ...current, startLocked: preview.startLocked === true } : current
      );
      setBumpPreview(preview);
    } catch (error: unknown) {
      notify(requestErrorMessage(error, "Failed to preview delivery stop"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const openCancel = async (stop: DeliveryStopRow) => {
    setBusy(true);
    try {
      const res = await httpClient(
        `${getApiUrl()}/fmbThaliDelivery/stops/${stop.id}/cancel-preview`
      );
      const preview = parseCancelPreview(res.json);
      if (!preview) {
        notify("Could not preview cancel", { type: "error" });
        return;
      }
      setCancelPreview({ stop, preview });
    } catch (error: unknown) {
      notify(requestErrorMessage(error, "Failed to preview cancel"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelPreview) {
      return;
    }
    setBusy(true);
    try {
      await httpClient(`${getApiUrl()}/fmbThaliDelivery/stops/${cancelPreview.stop.id}/cancel`, {
        method: "POST",
        headers: new Headers({ "Content-Type": "application/json" }),
      });
      notify(
        `Stop cancelled. Delivery resumes from ${formatListDate(
          cancelPreview.preview.resumeEffectiveDay,
          { empty: "—" }
        )}.`,
        { type: "success" }
      );
      setCancelPreview(null);
      await loadStops();
    } catch (error: unknown) {
      notify(requestErrorMessage(error, "Failed to cancel delivery stop"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (!thalis.length) {
    return (
      <Box sx={{ p: 1 }}>
        <Typography color="text.secondary">No thalis found for this FMB.</Typography>
      </Box>
    );
  }

  const bumpCopy = bumpPreview ? buildBumpConfirmCopy(bumpPreview) : null;
  const cancelCopy = cancelPreview ? buildCancelConfirmCopy(cancelPreview.preview) : null;

  return (
    <Box sx={{ p: 1 }}>
      {thalis.map((thali) => {
        const thaliStops = thali.id ? (stopsByThali.get(thali.id) ?? []) : [];
        const openStops = thaliStops.filter(isOpenDeliveryStop);
        const alert = deriveThaliDeliveryAlert(openStops);
        const atCap = !canScheduleAnotherStop(countOpenStops(openStops));

        return (
          <Paper key={thali.id} sx={{ p: 1.5, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="subtitle2">
                {thali.thaliNo}
                {thali.thaliType?.name ? ` — ${thali.thaliType.name}` : ""}
              </Typography>
              {!thali.isActive ? (
                <Typography variant="caption" color="text.secondary">
                  Inactive thali
                </Typography>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busy || !canWrite || atCap || !thali.id}
                  onClick={() => openCreate(thali)}
                >
                  Schedule a stop
                </Button>
              )}
            </Box>
            {thali.isActive && atCap ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                This thali already has {FMB_DELIVERY_STOP_MAX_OPEN} open stops. Cancel one to
                schedule another.
              </Typography>
            ) : null}
            <Alert severity={alert.severity} sx={{ mb: 1 }}>
              {alert.message}
            </Alert>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              History
            </Typography>
            {loading && thaliStops.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                Loading stop requests…
              </Typography>
            ) : thaliStops.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No delivery stop requests yet.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Logged at</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {thaliStops.map((stop) => (
                    <TableRow key={stop.id}>
                      <TableCell>{formatListDate(stop.startDate, { empty: "—" })}</TableCell>
                      <TableCell>{formatListDate(stop.endDate, { empty: "—" })}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={deliveryStopStatusLabel(stop.status)}
                          color={STATUS_CHIP_COLOR[stop.status]}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{stop.note ?? "—"}</TableCell>
                      <TableCell>{deliveryStopSourceLabel(stop.source)}</TableCell>
                      <TableCell>{formatDisplayDateTime(stop.createdAt, { empty: "—" })}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                          <Button
                            size="small"
                            disabled={busy || !canWrite || !isOpenDeliveryStop(stop)}
                            onClick={() => openEdit(thali, stop)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="warning"
                            disabled={busy || !canCancel || !isOpenDeliveryStop(stop)}
                            onClick={() => void openCancel(stop)}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        );
      })}

      <Dialog open={form != null} onClose={() => !busy && setForm(null)} fullWidth maxWidth="sm">
        <DialogTitle>{form?.mode === "edit" ? "Edit stop" : "Schedule a stop"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {form?.thali.thaliNo
                ? `Thali ${form.thali.thaliNo} will not be delivered on these dates.`
                : "This thali will not be delivered on these dates."}{" "}
              Maximum 90 days. Note is optional.
            </Typography>
            {form?.startLocked ? (
              <Alert severity="info">
                Days already past cut-off stay stopped. The from date cannot be changed.
              </Alert>
            ) : null}
            <YmdPicker
              label="From"
              value={form?.startDate ?? ""}
              disabled={form?.startLocked}
              onChange={(startDate) =>
                setForm((current) => (current ? { ...current, startDate } : current))
              }
            />
            <YmdPicker
              label="To"
              value={form?.endDate ?? ""}
              onChange={(endDate) =>
                setForm((current) => (current ? { ...current, endDate } : current))
              }
            />
            <TextField
              label="Note"
              value={form?.note ?? ""}
              onChange={(event) =>
                setForm((current) => (current ? { ...current, note: event.target.value } : current))
              }
              multiline
              minRows={2}
              inputProps={{ maxLength: 2000 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)} disabled={busy}>
            Back
          </Button>
          <Button variant="contained" onClick={() => void previewAndSave()} disabled={busy}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bumpPreview != null}
        onClose={() => !busy && setBumpPreview(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{bumpCopy?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{bumpCopy?.body}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBumpPreview(null)} disabled={busy}>
            Back
          </Button>
          <Button
            variant="contained"
            disabled={busy || !bumpPreview}
            onClick={() =>
              bumpPreview && void persistStop(bumpPreview, shouldConfirmBump(bumpPreview))
            }
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelPreview != null}
        onClose={() => !busy && setCancelPreview(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{cancelCopy?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{cancelCopy?.body}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelPreview(null)} disabled={busy}>
            Keep
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={busy}
            onClick={() => void confirmCancel()}
          >
            Yes, cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
